// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Column,
  Button,
  InlineNotification,
  Tag,
  Tile,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Modal,
  TextInput,
  DatePicker,
  DatePickerInput,
  TimePicker,
  TimePickerSelect,
  SelectItem,
  InlineLoading,
} from '@carbon/react';
import {
  Calendar,
  Task,
  Education,
  Activity,
  UserMultiple,
  CheckmarkFilled,
  Warning,
  Add,
  View,
} from '@carbon/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { useCollaboration } from '../../hooks/useCollaboration';
import CollaborationSession from '../../components/collaboration/CollaborationSession';

interface FamilyMember {
  id: string;
  name: string;
  role: 'parent' | 'child';
  avatar?: string;
  status: 'online' | 'busy' | 'offline';
  currentActivity?: string;
}

interface FamilyEvent {
  id: string;
  title: string;
  type: 'homework' | 'test' | 'activity' | 'meeting';
  date: Date;
  time: string;
  assignedTo: string[];
  status: 'upcoming' | 'in_progress' | 'completed';
  description?: string;
}

interface SharedResource {
  id: string;
  name: string;
  type: 'document' | 'schedule' | 'task' | 'note';
  sharedBy: string;
  sharedWith: string[];
  lastModified: Date;
  permissions: 'view' | 'edit';
}

const FamilyDashboardCollaboration: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [familyEvents, setFamilyEvents] = useState<FamilyEvent[]>([]);
  const [sharedResources, setSharedResources] = useState<SharedResource[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [newEventModalOpen, setNewEventModalOpen] = useState(false);
  const [newEvent, setNewEvent] = useState<Partial<FamilyEvent>>({});

  const { participants, isConnected, connectionError, sessionMetadata, sendEdit, createAnnotation } = useCollaboration({
    sessionId: `family-${user?.family_id}`,
    autoConnect: true,
    onConnect: () => console.log('Connected to family collaboration'),
    onError: error => console.error('Family collaboration error:', error),
  });

  useEffect(() => {
    const fetchFamilyData = async () => {
      try {
        // Fetch family members
        const membersResponse = await fetch(`/api/v1/families/${user?.family_id}/members`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        });
        if (membersResponse.ok) {
          const members = await membersResponse.json();
          setFamilyMembers(members);
        }

        // Fetch family events
        const eventsResponse = await fetch(`/api/v1/families/${user?.family_id}/events`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        });
        if (eventsResponse.ok) {
          const events = await eventsResponse.json();
          setFamilyEvents(events);
        }

        // Fetch shared resources
        const resourcesResponse = await fetch(`/api/v1/families/${user?.family_id}/resources`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        });
        if (resourcesResponse.ok) {
          const resources = await resourcesResponse.json();
          setSharedResources(resources);
        }
      } catch (error) {
        console.error('Error fetching family data:', error);
      }
    };

    if (user?.family_id) {
      fetchFamilyData();
    }
  }, [user]);

  const createEvent = async () => {
    try {
      const response = await fetch(`/api/v1/families/${user?.family_id}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(newEvent),
      });

      if (response.ok) {
        const event = await response.json();
        setFamilyEvents([...familyEvents, event]);
        setNewEventModalOpen(false);
        setNewEvent({});

        // Notify collaboration participants
        sendEdit(`New event created: ${event.title}`, 0);
      }
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  const getEventIcon = (type: FamilyEvent['type']) => {
    switch (type) {
      case 'homework':
        return <Education size={16} />;
      case 'test':
        return <Task size={16} />;
      case 'activity':
        return <Activity size={16} />;
      case 'meeting':
        return <UserMultiple size={16} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: FamilyMember['status']) => {
    switch (status) {
      case 'online':
        return 'green';
      case 'busy':
        return 'magenta';
      case 'offline':
        return 'cool-gray';
      default:
        return 'cool-gray';
    }
  };

  const upcomingEvents = familyEvents
    .filter(e => e.status === 'upcoming')
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 5);

  return (
    <div className="family-dashboard-collaboration">
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <div className="dashboard-header">
            <h1 className="cds--type-heading-04">Family Coordination Center</h1>
            <div className="connection-status">
              {isConnected ? (
                <Tag type="green">
                  <CheckmarkFilled size={16} />
                  Family Sync Active
                </Tag>
              ) : connectionError ? (
                <Tag type="red">
                  <Warning size={16} />
                  Sync Error
                </Tag>
              ) : (
                <InlineLoading status="active" description="Connecting to family sync..." />
              )}
            </div>
          </div>
        </Column>

        {connectionError && (
          <Column lg={16} md={8} sm={4}>
            <InlineNotification
              kind="error"
              title="Sync Error"
              subtitle="Family coordination features may be limited"
              lowContrast
            />
          </Column>
        )}

        <Column lg={16} md={8} sm={4}>
          <Tabs>
            <TabList aria-label="Family Dashboard Tabs">
              <Tab>Overview</Tab>
              <Tab>Schedule</Tab>
              <Tab>Tasks</Tab>
              <Tab>Resources</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Grid>
                  <Column lg={8} md={4} sm={4}>
                    <Tile>
                      <h3 className="cds--type-heading-03">Family Members</h3>
                      <div className="family-members-list">
                        {familyMembers.map(member => (
                          <div key={member.id} className="member-item">
                            <div className="member-info">
                              <span className="member-name">{member.name}</span>
                              <Tag type={getStatusColor(member.status)} size="sm">
                                {member.status}
                              </Tag>
                            </div>
                            {member.currentActivity && (
                              <span className="member-activity">{member.currentActivity}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </Tile>

                    <Tile className="family-schedule-tile">
                      <h3 className="cds--type-heading-03">Today's Schedule</h3>
                      <div className="schedule-list">
                        {upcomingEvents.map(event => (
                          <div key={event.id} className="schedule-item">
                            <div className="event-time">{event.time}</div>
                            <div className="event-details">
                              <div className="event-title">
                                {getEventIcon(event.type)}
                                <span>{event.title}</span>
                              </div>
                              <div className="event-assignees">
                                {event.assignedTo.map(id => {
                                  const member = familyMembers.find(m => m.id === id);
                                  return member ? (
                                    <Tag key={id} type="outline" size="sm">
                                      {member.name}
                                    </Tag>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Tile>
                  </Column>

                  <Column lg={8} md={4} sm={4}>
                    <CollaborationSession
                      sessionId={`family-${user?.family_id}`}
                      userId={user?.id || ''}
                      token={user?.token || ''}
                      mode="participant"
                      sessionTitle="Family Coordination"
                    >
                      <Tile>
                        <h3 className="cds--type-heading-03">Family Notes & Reminders</h3>
                        <div className="family-notes">
                          <div className="notes-editor">
                            <textarea
                              placeholder="Add a family note or reminder..."
                              onChange={e => sendEdit(e.target.value, 0)}
                              className="family-notes-textarea"
                              aria-label="Add a family note or reminder"
                            />
                          </div>
                          <Button
                            kind="primary"
                            size="sm"
                            onClick={() => createAnnotation('note', 'Family reminder', { start: 0, end: 10 })}
                          >
                            Add Note
                          </Button>
                        </div>
                      </Tile>

                      <Tile className="family-activities-tile">
                        <h3 className="cds--type-heading-03">Recent Activities</h3>
                        <div className="activity-feed">
                          {participants.map(p => (
                            <div key={p.id} className="activity-item">
                              <span className="activity-user">{p.name}</span>
                              <span className="activity-action">is viewing the dashboard</span>
                              <span className="activity-time">now</span>
                            </div>
                          ))}
                        </div>
                      </Tile>
                    </CollaborationSession>
                  </Column>
                </Grid>
              </TabPanel>

              <TabPanel>
                <div className="schedule-panel">
                  <div className="schedule-header">
                    <h3 className="cds--type-heading-03">Family Schedule</h3>
                    <Button kind="primary" size="sm" renderIcon={Add} onClick={() => setNewEventModalOpen(true)}>
                      Add Event
                    </Button>
                  </div>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeader>Date</TableHeader>
                          <TableHeader>Time</TableHeader>
                          <TableHeader>Event</TableHeader>
                          <TableHeader>Type</TableHeader>
                          <TableHeader>Assigned To</TableHeader>
                          <TableHeader>Status</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {familyEvents.map(event => (
                          <TableRow key={event.id}>
                            <TableCell>{new Date(event.date).toLocaleDateString()}</TableCell>
                            <TableCell>{event.time}</TableCell>
                            <TableCell>{event.title}</TableCell>
                            <TableCell>
                              <Tag type="outline">
                                {getEventIcon(event.type)}
                                {event.type}
                              </Tag>
                            </TableCell>
                            <TableCell>
                              {event.assignedTo
                                .map(id => {
                                  const member = familyMembers.find(m => m.id === id);
                                  return member?.name;
                                })
                                .join(', ')}
                            </TableCell>
                            <TableCell>
                              <Tag type={event.status === 'completed' ? 'green' : 'blue'}>{event.status}</Tag>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </TabPanel>

              <TabPanel>
                <div className="tasks-panel">
                  <h3 className="cds--type-heading-03">Family Tasks</h3>
                  <div className="tasks-grid">
                    {['To Do', 'In Progress', 'Completed'].map(status => (
                      <div key={status} className="task-column">
                        <h4 className="cds--type-heading-02">{status}</h4>
                        <div className="task-list">
                          {/* Task items would go here */}
                          <div className="task-item">
                            <span>Sample task</span>
                            <Tag size="sm">John</Tag>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabPanel>

              <TabPanel>
                <div className="resources-panel">
                  <h3 className="cds--type-heading-03">Shared Resources</h3>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeader>Resource</TableHeader>
                          <TableHeader>Type</TableHeader>
                          <TableHeader>Shared By</TableHeader>
                          <TableHeader>Last Modified</TableHeader>
                          <TableHeader>Actions</TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sharedResources.map(resource => (
                          <TableRow key={resource.id}>
                            <TableCell>{resource.name}</TableCell>
                            <TableCell>
                              <Tag type="outline">{resource.type}</Tag>
                            </TableCell>
                            <TableCell>{resource.sharedBy}</TableCell>
                            <TableCell>{new Date(resource.lastModified).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <Button kind="ghost" size="sm" renderIcon={View}>
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Column>
      </Grid>

      <Modal
        open={newEventModalOpen}
        onRequestClose={() => setNewEventModalOpen(false)}
        modalHeading="Create Family Event"
        primaryButtonText="Create"
        secondaryButtonText="Cancel"
        onRequestSubmit={createEvent}
      >
        <TextInput
          id="event-title"
          labelText="Event Title"
          placeholder="Enter event title"
          value={newEvent.title || ''}
          onChange={e => setNewEvent({ ...newEvent, title: e.target.value })}
        />
        <DatePicker datePickerType="single">
          <DatePickerInput
            id="event-date"
            labelText="Date"
            placeholder="mm/dd/yyyy"
            onChange={e => setNewEvent({ ...newEvent, date: new Date(e.target.value) })}
          />
        </DatePicker>
        <TimePicker id="event-time" labelText="Time">
          <TimePickerSelect id="time-picker-select" labelText="Time">
            <SelectItem value="09:00" text="9:00 AM" />
            <SelectItem value="10:00" text="10:00 AM" />
            <SelectItem value="11:00" text="11:00 AM" />
            <SelectItem value="12:00" text="12:00 PM" />
            <SelectItem value="13:00" text="1:00 PM" />
            <SelectItem value="14:00" text="2:00 PM" />
            <SelectItem value="15:00" text="3:00 PM" />
            <SelectItem value="16:00" text="4:00 PM" />
            <SelectItem value="17:00" text="5:00 PM" />
          </TimePickerSelect>
        </TimePicker>
      </Modal>
    </div>
  );
};

export default FamilyDashboardCollaboration;
